import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import {
	type AdminBrandDetail,
	listAdminBrandRows,
	mergeAdminBrands,
	previewBrandMerge,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";
import { useDebounce } from "@/lib/use-debounce";

/**
 * Plan 008 M4: confirm a brand merge by showing what it will do first.
 *
 * A merge re-points every product a brand owns and cannot be undone by pressing
 * the same button again, so the confirmation is not ceremony -- it is the only
 * place the operator can see the SKU collisions that would otherwise surface as
 * a failure after committing.
 *
 * The preview is ADVISORY and the wording says so ("would", not "will"). It
 * takes no database locks, because holding them while someone reads a dialog
 * would block every other write against those brands. The server re-runs the
 * same assessment under lock when the merge actually happens -- so a preview
 * gone stale costs a clear refusal, not a wrong merge.
 */

const TITLE_ID = "merge-brand-dialog-title";
const LIST_ID = "merge-target-listbox";

export function MergeBrandDialog({
	brand,
	onClose,
	onMerged,
}: {
	brand: AdminBrandDetail;
	onClose: () => void;
	/*
	 * Receives what actually moved, not just the fact that it did.
	 *
	 * The preview's count is advisory -- a product filed under the loser between
	 * preview and merge moves too -- and the server returns the real number for
	 * exactly that reason. Discarding it here left the operator with a figure
	 * they were shown BEFORE the merge and no way to notice it was wrong, which
	 * is the one part of the confirmation that could be contradicted in silence.
	 */
	onMerged: (result: { movedProducts: number }) => void;
}) {
	/*
	 * The chosen target, with its name, so the dialog can keep showing it after
	 * the search that found it has moved on.
	 */
	const [target, setTarget] = useState<{ id: string; name: string } | null>(
		null,
	);
	const targetId = target?.id ?? null;
	const [error, setError] = useState<string | null>(null);

	/*
	 * SEARCHED, not listed.
	 *
	 * This fetched one page and filtered it in the browser. Plan 006's brand
	 * master is 861 rows and a page is 25, so the target an operator wanted was
	 * usually absent -- and a dropdown showing 25 of 861 gives no sign of it,
	 * reading as "these are the brands". That made the merge tool unusable for
	 * the case it exists for: consolidating duplicates in a large master.
	 *
	 * `q` goes to the server, like the category picker's typeahead and for the
	 * same reason: past a screenful a select is a scrolling exercise, and someone
	 * merging "Somany Impressa" already knows the name they are looking for.
	 *
	 * ONE control. The search box used to feed a separate, closed dropdown, so
	 * typing changed nothing on screen until the operator thought to open the
	 * dropdown -- QA read that, reasonably, as a search box that did nothing.
	 * The results now render as a list directly under the box.
	 */
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search, 300);
	/*
	 * Which result the arrow keys are on. -1 is "none", so Enter never commits a
	 * brand the operator has not looked at.
	 */
	const [activeIndex, setActiveIndex] = useState(-1);

	const {
		data: candidates,
		isFetching: searching,
		isError: searchFailed,
		refetch: retrySearch,
	} = useQuery({
		queryKey: ["admin-brands", "merge-targets", debouncedSearch],
		queryFn: () =>
			listAdminBrandRows({
				isActive: true,
				q: debouncedSearch || undefined,
			}),
	});

	// Live, not superseded, and not this brand. The server refuses the rest
	// anyway; filtering here means the operator is not offered a choice that is
	// going to be rejected.
	const options = (candidates?.items ?? []).filter(
		(candidate) => candidate.id !== brand.id && !candidate.mergedIntoId,
	);

	/*
	 * Hidden means "matched but did not FIT ON THE PAGE" -- so subtract the page,
	 * not the list left after filtering.
	 *
	 * Subtracting `options.length` counted the rows this component removed itself.
	 * `listBrandsForAdmin` builds `total` from `isActive` and the name filter and
	 * never excludes superseded brands, so a search matching only this brand, or
	 * only duplicates already merged away, reported "1 more brand matches. Narrow
	 * the search to reach them." -- pointing the operator at a row no search can
	 * ever surface. Worse for the case this dialog exists for: consolidating
	 * near-duplicates is exactly when merged-away rows cluster under the term
	 * being typed.
	 */
	const shownOnPage = candidates?.items.length ?? 0;
	const hiddenMatches = Math.max(0, (candidates?.total ?? 0) - shownOnPage);

	const { data: preview, isFetching: previewing } = useQuery({
		queryKey: ["merge-preview", brand.id, targetId],
		queryFn: () => previewBrandMerge(brand.id, targetId as string),
		enabled: Boolean(targetId),
	});

	const merge = useMutation({
		mutationFn: () => mergeAdminBrands(brand.id, targetId as string),
		// Called with the result alone. Handing `onSuccess` straight to the prop
		// also passes React Query's variables, context and mutation object, which
		// are not part of this component's contract and would quietly become part
		// of it the moment a caller read a second argument.
		onSuccess: (result) => onMerged(result),
		onError: (err: unknown) => {
			/*
			 * The server's own words, deliberately.
			 *
			 * This is the path where the preview was stale -- a brand withdrawn, or
			 * a colliding product created, between preview and merge. The refusal
			 * names which SKUs clash or which brand changed, and a generic "merge
			 * failed" would throw away the only part the operator can act on.
			 *
			 * Through `apiErrorMessage`, because a schema rejection puts a constant
			 * "Validation error" in `message` and the field-level detail elsewhere.
			 */
			setError(apiErrorMessage(err, "The merge was refused."));
		},
	});

	const blocked = Boolean(preview?.blocker) || preview?.collisions.length !== 0;

	function choose(option: { id: string; name: string }): void {
		setTarget({ id: option.id, name: option.name });
		setActiveIndex(-1);
		setError(null);
	}

	return (
		// The shared shell, which plan 008 asked for and this file was waiting on.
		// Escape, the focus trap and the focus restore come with it -- and this is
		// the dialog they mattered most for: without them a keyboard operator
		// could Tab out of a brand MERGE into the page behind it.
		<Dialog
			open
			titleId={TITLE_ID}
			/*
			 * Escape is refused while the merge is in flight, matching the Cancel
			 * button beside it, which is `disabled={merge.isPending}`. Before the
			 * shared shell this dialog had no Escape handler at all -- so adding
			 * one handed back exactly the exit the disabled button exists to
			 * remove, and dismissing mid-merge leaves the operator with no way to
			 * see what the merge did.
			 */
			onClose={() => {
				if (!merge.isPending) onClose();
			}}
			className="grid w-full max-w-lg gap-4 rounded-3xl border bg-white p-5 shadow-xl"
		>
			<div>
				<h2 id={TITLE_ID} className="text-lg font-medium">
					Merge “{brand.name}”
				</h2>
				<p className="text-sm text-muted-foreground">
					Its products move to the brand you choose. “{brand.name}” is kept and
					marked as merged, so the change stays traceable — it is not deleted.
				</p>
			</div>

			<dl className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 text-sm">
				<dt className="text-muted-foreground">From</dt>
				<dd className="font-medium">{brand.name}</dd>
				{target ? (
					<>
						<dt className="text-muted-foreground">Into</dt>
						<dd className="flex items-center justify-between gap-2">
							<span className="font-medium">{target.name}</span>
							{/*
							 * Changing the target clears it rather than leaving it chosen
							 * behind a new search -- a target the operator can no longer
							 * see must not keep its preview and an enabled Merge button.
							 */}
							<Button
								variant="outline"
								size="sm"
								disabled={merge.isPending}
								onClick={() => {
									setTarget(null);
									setError(null);
								}}
							>
								Change
							</Button>
						</dd>
					</>
				) : null}
			</dl>

			{target ? null : (
				<div className="grid gap-1.5">
					<Label htmlFor="merge-target">Merge into</Label>
					<Input
						id="merge-target"
						placeholder="Search brands by name"
						value={search}
						role="combobox"
						aria-expanded={options.length > 0}
						aria-controls={LIST_ID}
						aria-autocomplete="list"
						aria-activedescendant={
							activeIndex >= 0 ? `${LIST_ID}-option-${activeIndex}` : undefined
						}
						onChange={(event) => {
							setSearch(event.target.value);
							setActiveIndex(-1);
						}}
						onKeyDown={(event) => {
							if (options.length === 0) return;
							if (event.key === "ArrowDown") {
								event.preventDefault();
								setActiveIndex((index) => (index + 1) % options.length);
							} else if (event.key === "ArrowUp") {
								event.preventDefault();
								setActiveIndex(
									(index) => (index <= 0 ? options.length : index) - 1,
								);
							} else if (event.key === "Enter" && activeIndex >= 0) {
								event.preventDefault();
								choose(options[activeIndex]);
							}
						}}
					/>
					{searchFailed ? (
						// A failed search is not an empty one: "No live brands match"
						// here would tell the operator the brand does not exist.
						<p role="alert" className="text-xs text-red-700">
							Could not search brands.{" "}
							<button
								type="button"
								className="underline"
								onClick={() => retrySearch()}
							>
								Try again
							</button>
						</p>
					) : options.length > 0 ? (
						<div
							id={LIST_ID}
							role="listbox"
							aria-label="Brands to merge into"
							className="max-h-56 overflow-auto rounded-md border"
						>
							<ul>
								{options.map((option, index) => (
									<li key={option.id}>
										<button
											type="button"
											id={`${LIST_ID}-option-${index}`}
											role="option"
											aria-selected={index === activeIndex}
											className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-100 focus:bg-slate-100 focus:outline-none${
												index === activeIndex ? " bg-slate-100" : ""
											}`}
											onMouseEnter={() => setActiveIndex(index)}
											onClick={() => choose(option)}
										>
											{option.name}
										</button>
									</li>
								))}
							</ul>
						</div>
					) : searching ? (
						<p className="text-xs text-muted-foreground">Searching…</p>
					) : null}
					{/*
					 * "Nothing here" and "more than fits" are different answers, and the
					 * page can be non-empty while none of it is a legal target -- a page
					 * of brands already merged away. Each case says which it is.
					 */}
					{searching || searchFailed ? null : options.length === 0 &&
						hiddenMatches === 0 ? (
						<p className="text-xs text-muted-foreground">
							No live brands match that search.
						</p>
					) : options.length === 0 ? (
						<p className="text-xs text-muted-foreground">
							Nothing on this page can be merged into, and {hiddenMatches} more
							brand{hiddenMatches === 1 ? "" : "s"} match. Narrow the search.
						</p>
					) : hiddenMatches > 0 ? (
						<p className="text-xs text-muted-foreground">
							{hiddenMatches} more brand{hiddenMatches === 1 ? "" : "s"} match.
							Narrow the search to reach them.
						</p>
					) : null}
				</div>
			)}

			{targetId ? (
				previewing ? (
					<p className="text-sm text-muted-foreground">Checking…</p>
				) : preview ? (
					<div className="grid gap-2 rounded-2xl border bg-slate-50/80 p-3 text-sm">
						{preview.blocker ? (
							<p role="alert" className="text-amber-900">
								{preview.blocker}
							</p>
						) : preview.collisions.length > 0 ? (
							<div role="alert" className="grid gap-1 text-amber-900">
								<p>
									Both brands already use{" "}
									{preview.moreCollisions
										? "these brand SKUs, among others"
										: "these brand SKUs"}
									. Resolve the duplicate products first:
								</p>
								{/*
								 * The SKUs themselves, not a count. Resolving this means
								 * finding those specific products, and a number does not
								 * locate them -- which is the whole reason the preview
								 * returns a list.
								 */}
								<ul className="list-inside list-disc font-mono text-xs">
									{preview.collisions.map((sku) => (
										<li key={sku}>{sku}</li>
									))}
								</ul>
							</div>
						) : (
							<p>
								<strong>{preview.movedProducts}</strong> product
								{preview.movedProducts === 1 ? "" : "s"} would move to “
								{preview.winner.name}”.
							</p>
						)}
					</div>
				) : null
			) : null}

			{error ? (
				<p role="alert" className="text-sm text-amber-900">
					{error}
				</p>
			) : null}

			<div className="flex justify-end gap-2 border-t pt-3">
				{/*
				 * Disabled while the merge is in flight, because it cannot do what
				 * it appears to do. There is no AbortSignal on `mergeAdminBrands`,
				 * and React Query runs the mutation's `onSuccess` whether or not the
				 * component is still mounted -- so clicking Cancel here closed the
				 * dialog while the merge went on to commit, with nothing left on
				 * screen to report it. A control that reads as "stop" must either
				 * stop something or not be offered.
				 */}
				<Button variant="outline" disabled={merge.isPending} onClick={onClose}>
					Cancel
				</Button>
				<Button
					disabled={!targetId || previewing || blocked || merge.isPending}
					onClick={() => {
						setError(null);
						merge.mutate();
					}}
				>
					{merge.isPending ? "Merging…" : "Merge"}
				</Button>
			</div>
		</Dialog>
	);
}
